import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ShieldCheck, ArrowUpRight, Search, X, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["All", "Products", "Services"];

export default function Businesses({ onJoin }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/businesses/cities`).then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (category !== "All") params.category = category;
    if (search.trim()) params.search = search.trim();
    if (city && city !== "all") params.city = city;
    if (verifiedOnly) params.verified = true;
    setLoading(true);
    const t = setTimeout(() => {
      axios
        .get(`${API}/businesses`, { params })
        .then((r) => setItems(r.data))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [category, search, city, verifiedOnly]);

  const clearFilters = () => {
    setCategory("All");
    setSearch("");
    setCity("all");
    setVerifiedOnly(false);
  };

  const hasFilters = category !== "All" || search || (city && city !== "all") || verifiedOnly;

  return (
    <section id="businesses" className="py-24 md:py-32 bg-brand-paper2" data-testid="businesses-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine">
            Explore the Exchange
          </span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-brand-ink">
            Discover suppliers <span className="italic text-brand-wine">you can trust</span>.
          </h2>
          <p className="mt-5 text-brand-muted text-lg max-w-xl leading-relaxed">
            Search and filter our curated women-led business directory — every profile is AI-standardized and reviewed by our curation team.
          </p>
        </div>

        {/* Search + filters */}
        <div className="mt-10 bg-white rounded-2xl border border-brand-line p-4 md:p-5 flex flex-col lg:flex-row gap-3 lg:items-center" data-testid="business-filter-bar">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search businesses, capabilities, keywords…"
              className="w-full bg-brand-paper2/60 border border-brand-line rounded-xl pl-11 pr-10 py-3 h-[46px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all"
              data-testid="search-input"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-wine"
                aria-label="Clear search"
                data-testid="search-clear-btn"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-brand-paper2/60 border border-brand-line rounded-full p-1" data-testid="business-filters">
              {CATEGORIES.map((f) => (
                <button
                  key={f}
                  onClick={() => setCategory(f)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                    category === f
                      ? "bg-brand-wine text-white"
                      : "text-brand-ink hover:text-brand-wine"
                  }`}
                  data-testid={`filter-${f.toLowerCase()}`}
                >
                  {f}
                </button>
              ))}
            </div>

            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-[180px] bg-brand-paper2/60 border-brand-line rounded-xl h-[46px]" data-testid="city-filter">
                <SelectValue placeholder="All cities" />
              </SelectTrigger>
              <SelectContent className="bg-white border-brand-line max-h-72">
                <SelectItem value="all" data-testid="city-option-all">All cities</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`city-option-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <label className="flex items-center gap-2 bg-brand-paper2/60 border border-brand-line rounded-full h-[46px] px-4 cursor-pointer">
              <ShieldCheck size={14} className="text-brand-wine" />
              <span className="text-sm font-medium">Verified only</span>
              <Switch
                checked={verifiedOnly}
                onCheckedChange={setVerifiedOnly}
                className="data-[state=checked]:bg-brand-wine"
                data-testid="verified-toggle"
              />
            </label>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-brand-muted hover:text-brand-wine underline underline-offset-4"
                data-testid="clear-filters-btn"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-sm text-brand-muted" data-testid="results-count">
          {loading ? "Searching…" : `${items.length} ${items.length === 1 ? "business" : "businesses"} found`}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[420px] rounded-2xl bg-white/60 border border-brand-line animate-pulse" />
            ))}

          {!loading && items.length === 0 && (
            <div className="col-span-full text-center py-20 text-brand-muted">
              No businesses match your filters. Try clearing them.
            </div>
          )}

          {!loading && items.map((b, i) => (
            <motion.article
              key={b.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group bg-white rounded-2xl overflow-hidden border border-brand-line hover:-translate-y-1 hover:shadow-[0_25px_50px_-25px_rgba(139,30,63,0.25)] transition-all duration-500"
              data-testid={`business-card-${b.id}`}
            >
              <Link to={`/business/${b.id}`} className="block">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={b.image}
                    alt={b.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  {b.verified && (
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full px-3 py-1 flex items-center gap-1.5 text-xs font-semibold text-brand-wine">
                      <ShieldCheck size={14} />
                      Verified
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-brand-wine text-white rounded-full px-3 py-1 text-[11px] uppercase tracking-widest">
                    {b.category}
                  </div>
                  {b.readiness_score !== null && b.readiness_score !== undefined && (
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-full px-3 py-1 flex items-center gap-1.5 text-xs font-semibold text-brand-ink">
                      <Sparkles size={13} className="text-brand-wine" />
                      {b.readiness_score}/100
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.2em] text-brand-muted">
                        {b.tag}
                      </span>
                      <h3 className="mt-1 font-serif text-2xl font-semibold text-brand-ink leading-snug">
                        {b.name}
                      </h3>
                    </div>
                    <span className="w-9 h-9 rounded-full border border-brand-line flex items-center justify-center text-brand-wine group-hover:bg-brand-wine group-hover:text-white group-hover:border-brand-wine transition-colors">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                  {b.tagline ? (
                    <p className="mt-3 text-[14px] text-brand-wine italic font-serif leading-snug line-clamp-2">
                      &ldquo;{b.tagline}&rdquo;
                    </p>
                  ) : (
                    <p className="mt-3 text-[14px] text-brand-muted leading-relaxed line-clamp-2">
                      {b.description}
                    </p>
                  )}
                  <div className="mt-5 flex items-center gap-2 text-sm text-brand-muted">
                    <MapPin size={14} />
                    {b.location}
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 text-center">
          <button
            onClick={onJoin}
            className="inline-flex items-center gap-2 bg-brand-wine text-white rounded-lg px-7 py-3.5 font-medium text-[15px] hover:bg-brand-wineDark transition-all"
            data-testid="businesses-list-cta"
          >
            List your business
          </button>
        </div>
      </div>
    </section>
  );
}
